import React, { useEffect, useState } from "react";
import { Users, FileText, Gift, CheckCircle2, Clock } from "lucide-react";
import api from "@/lib/api";
import { Link } from "react-router-dom";

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="bento p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-5">
        <p className="eyebrow">{label}</p>
        <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
          <Icon className="w-4 h-4 text-slate-900" />
        </div>
      </div>
      <p className="text-5xl font-bold tracking-tight text-slate-900">{value}</p>
      {accent && <p className="text-xs text-slate-500 mt-2">{accent}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch (err) {
        console.error("Failed to load dashboard stats:", err);
      }
    })();
  }, []);

  const s = stats || {};
  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="eyebrow mb-2">Overview</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="dashboard-title">Dashboard</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={Users} label="Tenants" value={s.clients ?? "—"} accent="Active tenants onboarded" testId="stat-clients" />
        <StatCard icon={FileText} label="Contracts signed" value={s.contracts_signed ?? "—"} accent={`${s.contracts_total ?? 0} total`} testId="stat-contracts" />
        <StatCard icon={Gift} label="Referral conversions" value={s.referral_conversions ?? "—"} accent={`${s.referral_clicks ?? 0} clicks`} testId="stat-referrals" />
        <StatCard icon={Clock} label="Pending approvals" value={s.pending_approvals ?? "—"} accent="Awaiting tenant sign-off" testId="stat-pending" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bento p-8 lg:col-span-2" data-testid="activity-card">
          <p className="eyebrow mb-4">Recent activity</p>
          <div className="space-y-3">
            {(s.recent_activity || []).length === 0 ? (
              <p className="text-sm text-slate-500">No activity yet. Onboard your first tenant to get started.</p>
            ) : (
              (s.recent_activity || []).map((a) => (
                <div key={a.id} className="flex items-center gap-3 py-2 border-b last:border-0 border-slate-100">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{a.action}</p>
                    <p className="text-xs text-slate-500 font-mono">{a.actor}</p>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">{new Date(a.timestamp).toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bento p-8 bg-slate-900 text-white grain-overlay" data-testid="quick-actions-card">
          <p className="eyebrow text-slate-400 mb-4">Jump right in</p>
          <h3 className="text-2xl font-bold mb-6">Get started</h3>
          <div className="space-y-3">
            <Link to="/agency/clients" className="block px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium" data-testid="quick-add-client">→ Add a tenant</Link>
            <Link to="/agency/contracts" className="block px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium" data-testid="quick-new-contract">→ New contract</Link>
            <Link to="/agency/settings" className="block px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium" data-testid="quick-brand-setup">→ Set up branding & SMTP</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
